import { Random, GenData } from '@educraft/base';
import {
  PageGen,
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PdfPen,
  PdfFont,
  ETextAlign,
} from '../pdf';
import { concatTransformationMatrix } from 'pdf-lib';
import { GUtil } from '../gutil';
import { placeObjects as placeObjectsCore } from '../../core/placeObjects';
import '../draw/primitives';
import '../draw/string';
import '../draw/helpers';
import '../draw/cut';
import '../draw/label';
import { GLabel, GLabelType, GTuple } from '../glabel';
import { MM } from '../../modules/ui/controls/primitives';

/**
 * Logico puzzle variants
 */
export enum ELogicoVariant {
  Piccolo = 0, // 10 answers
  Primo = 1, // 6 answers
  Maximo = 2, // 18 answers
}

/**
 * Logico layout modes
 */
export enum ELogicoMode {
  One = 0, // Single large area
  Mini = 1, // Mini items in grid
  Rows = 2, // Single column rows
  TwoColumns = 3, // Two column grid
  Top = 4, // Question on top
  Half = 5, // Question takes half
  Mode23 = 6, // 2-3-2-3 pattern
  Pyramid = 7, // Pyramid pattern
  Mode343 = 8, // 3-4-3 pattern
  Random = 9, // Random placement
  Path0 = 10, // Path pattern 0
  Path1 = 11, // Path pattern 1
  Path2 = 12, // Path pattern 2
  Around = 13, // Around pattern
  Diagonal = 14, // Diagonal pattern
  Aside = 15, // Aside pattern
  MiniRandom = 16, // Mini random pattern
  ZigZag1 = 17, // Zigzag pattern 1
  ZigZag2 = 18, // Zigzag pattern 2
  PaperA5 = 19, // A5 paper mode (landscape A4 split in half)
}

/**
 * Logico color schemes.
 * Colors are CMYK→RGB conversions from C# GUtil: R=255*(1-C)*(1-K), G=255*(1-M)*(1-K), B=255*(1-Y)*(1-K)
 */
export const PiccoloColors: PdfColor[] = [
  PdfColor.fromHex('#FF0000'), // (0,1,1,0) Red
  PdfColor.fromHex('#FF6600'), // (0,0.6,1,0) Orange
  PdfColor.fromHex('#FFFF00'), // (0,0,1,0) Yellow
  PdfColor.fromHex('#00FF00'), // (1,0,1,0) Green
  PdfColor.fromHex('#00FFFF'), // (1,0,0,0) Cyan
];

export const PrimoColors: PdfColor[] = [
  PdfColor.fromHex('#FF0000'), // (0,1,1,0) Red
  PdfColor.fromHex('#FF6600'), // (0,0.6,1,0) Orange
  PdfColor.fromHex('#FFFF00'), // (0,0,1,0) Yellow
  PdfColor.fromHex('#00FF00'), // (1,0,1,0) Green
  PdfColor.fromHex('#00FFFF'), // (1,0,0,0) Cyan
  PdfColor.fromHex('#FF66FF'), // (0,0.6,0,0) Pink
];

export const MaximoColors: PdfColor[] = [
  PdfColor.fromHex('#FF0000'), // (0,1,1,0)   Red
  PdfColor.fromHex('#FF6600'), // (0,0.6,1,0) Orange
  PdfColor.fromHex('#FFFF00'), // (0,0,1,0)   Yellow
  PdfColor.fromHex('#00FF00'), // (1,0,1,0)   Green
  PdfColor.fromHex('#006600'), // (1,0,1,0.6) Dark Green
  PdfColor.fromHex('#00FFFF'), // (1,0,0,0)   Cyan
  PdfColor.fromHex('#0000FF'), // (1,1,0,0)   Blue
  PdfColor.fromHex('#665252'), // (0,0.2,0.2,0.6) Brownish
  PdfColor.fromHex('#FF66FF'), // (0,0.6,0,0) Pink
];

/**
 * Logico puzzle sizes (in mm) — from C# GUtil.PiccoloSize/PrimoSize/MaximoSize
 */
export const PiccoloSize = new PdfSize(PdfUnit.FromMillimeters(164), PdfUnit.FromMillimeters(224));
export const PrimoSize = new PdfSize(PdfUnit.FromMillimeters(162), PdfUnit.FromMillimeters(224));
export const MaximoSize = new PdfSize(PdfUnit.FromMillimeters(175), PdfUnit.FromMillimeters(236));

/**
 * Get answer count for variant
 */
export function logicoCount(variant: ELogicoVariant): number {
  switch (variant) {
    case ELogicoVariant.Piccolo:
      return 10;
    case ELogicoVariant.Primo:
      return 6;
    case ELogicoVariant.Maximo:
      return 18;
  }
}

/**
 * Get mini cell count for variant
 */
export function logicoMiniCellCount(variant: ELogicoVariant): { x: number; y: number } {
  switch (variant) {
    case ELogicoVariant.Piccolo:
      return { x: 2, y: 5 };
    case ELogicoVariant.Primo:
      return { x: 2, y: 3 };
    case ELogicoVariant.Maximo:
      return { x: 2, y: 9 };
  }
}

/**
 * Get logico size for variant
 */
export function logicoSize(variant: ELogicoVariant): PdfSize {
  switch (variant) {
    case ELogicoVariant.Piccolo:
      return PiccoloSize;
    case ELogicoVariant.Primo:
      return PrimoSize;
    case ELogicoVariant.Maximo:
      return MaximoSize;
  }
}

/**
 * Get colors for variant
 */
export function logicoColors(variant: ELogicoVariant): PdfColor[] {
  switch (variant) {
    case ELogicoVariant.Piccolo:
      return PiccoloColors;
    case ELogicoVariant.Primo:
      return PrimoColors;
    case ELogicoVariant.Maximo:
      return MaximoColors;
  }
}

/**
 * Label data for Logico
 */
export interface LogicoLabel {
  text: string;
}

/**
 * Data passed to drawing callbacks
 */
export interface LogicoData {
  page: PageGen;
  rect: PdfRect;
  index: number;
  invMap: number[];
  isFront: boolean; // true for page 1, false for page 2
  isQuestion: boolean;
  variant: ELogicoVariant;
  subIndex?: number; // For multi-part questions (Aside mode)
}

/**
 * Generate a shuffled index map
 */
export function genMap(count: number, random: Random): number[] {
  const arr = Array.from({ length: count }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = random.next(0, i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Draw Logico rectangle answer indicator
 */
export function drawLogicoRect(
  page: PageGen,
  variant: ELogicoVariant,
  index: number,
  rect: PdfRect
): void {
  const colors = logicoColors(variant);

  switch (variant) {
    case ELogicoVariant.Piccolo: {
      const dx = PdfUnit.FromMillimeters(4);
      const colorIdx = Math.floor(index / 2) % colors.length;
      page.drawRectangle(null, new PdfBrush(colors[colorIdx]), rect);
      if (index % 2 === 1) {
        const inner = rect.deflate(dx);
        page.drawRoundedRectangle(null, PdfBrush.White, inner, dx);
      }
      break;
    }
    case ELogicoVariant.Maximo: {
      const dx = Math.min(rect.height / 4, PdfUnit.FromMillimeters(4));
      const colorIdx = Math.floor(index / 2) % colors.length;
      page.drawRectangle(null, new PdfBrush(colors[colorIdx]), rect);
      if (index % 2 === 1) {
        const inner = rect.deflate(dx);
        page.drawRoundedRectangle(null, PdfBrush.White, inner, dx);
      }
      break;
    }
    case ELogicoVariant.Primo: {
      const colorIdx = index % colors.length;
      page.drawRectangle(null, new PdfBrush(colors[colorIdx]), rect);
      break;
    }
  }
}

/**
 * Draw Piccolo answer (page 1 style)
 */
export function drawPiccoloAnswer1(
  page: PageGen,
  rect: PdfRect,
  label: GLabel,
  index: number,
  font?: PdfFont,
  singleLine?: boolean
): void {
  const f = font ?? GUtil.baseFont;

  page.drawLabel(label, { rect, font: f, singleLine });
  page.drawRectangle(GUtil.basePen, null, rect);
}

/**
 * Draw Piccolo answer (page 2 style)
 */
export function drawPiccoloAnswer2(
  page: PageGen,
  variant: ELogicoVariant,
  rect: PdfRect,
  label: GLabel,
  index: number,
  font?: PdfFont,
  singleLine?: boolean
): void {
  const f = font ?? GUtil.baseFont;

  drawLogicoRect(page, variant, index, rect);
  page.drawLabel(label, { rect, font: f, singleLine });

  page.drawRectangle(GUtil.basePen, null, rect);
}

/**
 * Draw Logico circle answer indicator
 */
export function drawLogicoCircle(
  page: PageGen,
  variant: ELogicoVariant,
  index: number,
  rect: PdfRect
): void {
  const colors = logicoColors(variant);
  let size = Math.min(rect.width, rect.height);
  size = Math.min(PdfUnit.FromMillimeters(10), size);

  const p = new PdfPoint(rect.centerX, rect.centerY);
  const circleRect = new PdfRect(p.x - size / 2, p.y - size / 2, size, size);

  switch (variant) {
    case ELogicoVariant.Piccolo:
    case ELogicoVariant.Maximo: {
      const colorIdx = Math.floor(index / 2) % colors.length;
      page.drawEllipse(GUtil.basePen, new PdfBrush(colors[colorIdx]), circleRect);
      if (index % 2 !== 0) {
        // C# uses inner = size / 2 (exactly half)
        const innerSize = size / 2;
        const innerRect = new PdfRect(
          p.x - innerSize / 2,
          p.y - innerSize / 2,
          innerSize,
          innerSize
        );
        page.drawEllipse(GUtil.basePen, PdfBrush.White, innerRect);
      }
      break;
    }
    case ELogicoVariant.Primo: {
      const colorIdx = index % colors.length;
      page.drawEllipse(GUtil.basePen, new PdfBrush(colors[colorIdx]), circleRect);
      break;
    }
  }
}

/**
 * Options for drawLogico function
 */
export interface DrawLogicoOptions {
  /**
   * Called to draw question text/image in the question area.
   * If not provided, draws GTuple.left using default label rendering.
   */
  drawQuestion?: (data: LogicoData) => void;

  /**
   * Called to draw answer text/image in the answer area.
   * If not provided, draws GTuple.right using default label rendering.
   */
  drawAnswer?: (data: LogicoData) => void;

  /**
   * Called when question area cell size is calculated (before drawing starts).
   * Receives the smallest available cell size for the current mode.
   */
  calcQuestionSize?: (size: PdfSize) => void;

  /**
   * Called when answer area size is calculated (before drawing starts).
   */
  calcAnswerSize?: (size: PdfSize) => void;

  /**
   * Page index to start drawing (default: 0).
   * Draws on pageIndex and pageIndex+1 (front and back).
   */
  pageIndex?: number;

  /**
   * Pre-calculated answer width. If not provided, calculated from labels.
   */
  answerWidth?: number;

  /**
   * Whether to draw outline borders (default: true).
   */
  drawOutline?: boolean;

  /**
   * Whether to draw answer circles (default: true).
   */
  drawCircle?: boolean;

  /**
   * ID string to print on page 2 (back side).
   */
  id?: string;

  /**
   * GenData instance (contains random, archive, and lang).
   */
  data?: GenData;

  /**
   * Forces answer labels to be drawn on a single line (no text wrapping).
   */
  singleLineAnswers?: boolean;
}

/**
 * Draw a complete Logico puzzle.
 *
 * Port of C# G<T>.DrawLogico()
 */
export function drawLogico(
  pdf: PdfGen,
  variant: ELogicoVariant,
  mode: ELogicoMode,
  tuples: GTuple[],
  map: number[],
  options: DrawLogicoOptions = {}
): void {
  const {
    drawQuestion,
    drawAnswer,
    calcQuestionSize,
    calcAnswerSize,
    pageIndex = 0,
    answerWidth: answerWidthOverride,
    drawOutline = true,
    drawCircle = true,
    id,
    data,
    singleLineAnswers,
  } = options;
  const page1 = pdf.getPage(pageIndex);
  const page2 = pdf.getPage(pageIndex + 1);

  // PaperA5 mode is handled completely differently - bypass all normal layout
  if (mode === ELogicoMode.PaperA5) {
    drawLogicoPaperA5(
      pdf,
      variant,
      tuples,
      map,
      pageIndex,
      answerWidthOverride,
      drawOutline,
      drawCircle,
      id,
      data,
      singleLineAnswers,
      drawQuestion,
      drawAnswer,
      calcQuestionSize,
      calcAnswerSize
    );
    return;
  }

  // Helper functions
  const setQuestionWidth = (size: PdfSize) => {
    calcQuestionSize?.(size);
  };

  const setAnswerSize = (size: PdfSize) => {
    calcAnswerSize?.(size);
  };

  // Alias for backwards compatibility
  const setWidth = setQuestionWidth;

  const sz = PdfUnit.FromMillimeters(10);
  const margin = PdfUnit.FromMillimeters(1);
  const borderOut = PdfUnit.FromMillimeters(4);
  const borderIn = PdfUnit.FromMillimeters(3);
  const questionHeight = PdfUnit.FromMillimeters(12);

  const miniCellCount = logicoMiniCellCount(variant);
  const variantSize = logicoSize(variant);
  const answerCount = logicoCount(variant);

  const optionsHeight = variantSize.height - 2 * borderOut - borderIn - questionHeight;
  const answerHeight = optionsHeight / answerCount;

  const sheetPoint = new PdfPoint(
    (PdfSize.A4.width - variantSize.width) / 2,
    (PdfSize.A4.height - variantSize.height) / 2
  );
  const optionPoint = new PdfPoint(
    sheetPoint.x + borderOut,
    sheetPoint.y + borderOut + questionHeight + borderIn
  );

  const sheetRect = new PdfRect(sheetPoint.x, sheetPoint.y, variantSize.width, variantSize.height);
  const questionRect = new PdfRect(
    sheetPoint.x + borderOut,
    sheetPoint.y + borderOut,
    variantSize.width - 2 * borderOut,
    questionHeight
  );

  // Calculate answer width
  let answerWidthReal = answerWidthOverride ?? answerHeight;
  for (const label of tuples) {
    const {right} = label;
    if (right.type === GLabelType.Text || right.type === GLabelType.ImageText) {
      // Image or ImageText
      const measured = page1.measureString(right.getText(), GUtil.baseFont);
      answerWidthReal = Math.max(answerWidthReal, measured.width);
    }
  }

  answerWidthReal = Math.min(answerHeight * 2, answerWidthReal);
  answerWidthReal = Math.max(answerWidthReal, MM(30));

  if (variant === ELogicoVariant.Maximo && answerWidthReal <= answerHeight * 2) {
    answerWidthReal = answerHeight * 2;
  }

  const optionsWidth = variantSize.width - 2 * borderOut - borderIn - answerWidthReal;
  const optionRect = new PdfRect(optionPoint.x, optionPoint.y, optionsWidth, optionsHeight);

  setAnswerSize(new PdfSize(answerWidthReal, answerHeight));

  // Compute a shared font for all answer labels so they render at a consistent size.
  // adjustFontMulti considers both cell width and height (with word-wrap).
  // Each text is its own inner array (one set per cell) so each is evaluated against one cell.
  // Use the deflated size (matching drawLabel's innerRect: deflate by 1mm since answerWidthReal >= 30mm).
  const allAnswerTexts = tuples
    .map((t) =>
      t.right.type === GLabelType.Text || t.right.type === GLabelType.ImageText
        ? [t.right.getText()]
        : null
    )
    .filter((t): t is string[] => t !== null);

  const answerLabelMargin = PdfUnit.FromMillimeters(1);
  const answerFont =
    allAnswerTexts.length > 0
      ? page1.adjustFontMulti(
          allAnswerTexts,
          GUtil.baseFont,
          new PdfSize(answerWidthReal - 2 * answerLabelMargin, answerHeight - 2 * answerLabelMargin)
        )
      : GUtil.baseFont;

  const answerPoint = new PdfPoint(
    sheetPoint.x + variantSize.width - borderOut - answerWidthReal,
    sheetPoint.y + borderOut + questionHeight + borderIn
  );

  // Generate inverse map (matches C# behavior: map[k] -> k, sorted by key)
  const invMap: number[] = map
    .map((val, idx) => ({ val, idx }))
    .sort((a, b) => a.val - b.val)
    .map((x) => x.idx);

  // Draw borders
  page1.drawRectangle(GUtil.cutPen, null, sheetRect);
  page1.drawRectangle(GUtil.basePen, null, sheetRect);

  // Draw ID on page 2 if provided
  if (id) {
    const idFont = new PdfFont(PdfFont.FONT_NAME, 8);
    page2.drawString(id, idFont, PdfBrush.Black, null, sheetRect, ETextAlign.BottomLeft);
  }

  // Draw question header
  page1.drawRectangle(GUtil.basePen, new PdfBrush(PdfColor.fromHex('#87CEEB')), questionRect);
  page2.drawRectangle(GUtil.basePen, new PdfBrush(PdfColor.fromHex('#D3D3D3')), questionRect);

  page1.drawString(
    data?.lang?.Logico_Match ?? 'Match',
    GUtil.baseFont,
    PdfBrush.Black,
    null,
    new PdfRect(
      sheetPoint.x + borderOut + borderIn,
      sheetPoint.y + borderOut,
      questionRect.width,
      questionHeight
    ),
    ETextAlign.CenterLeft
  );
  page2.drawString(
    data?.lang?.Logico_Match ?? 'Match',
    GUtil.baseFont,
    PdfBrush.Black,
    null,
    new PdfRect(
      sheetPoint.x + borderOut + borderIn,
      sheetPoint.y + borderOut,
      questionRect.width,
      questionHeight
    ),
    ETextAlign.CenterLeft
  );

  const d = drawCircle ? sz + margin * 2 : 0;

  // Default drawQ - renders label using GTuple.left
  const defaultDrawQuestion = (data: LogicoData) => {
    if (data.index < 0 || data.index >= tuples.length) return;
    const label = tuples[data.index];
    data.page.drawLabel(label.left, { rect: data.rect });
  };

  // Default drawAnswer - uses Piccolo answer drawing functions
  const defaultDrawAnswer = (data: LogicoData) => {
    if (data.index < 0 || data.index >= tuples.length) return;
    const label = tuples[data.index];

    if (data.isFront) {
      // For question page (page 1)
      drawPiccoloAnswer1(
        data.page,
        data.rect,
        label.right,
        data.index,
        answerFont,
        singleLineAnswers
      );
    } else {
      // For answer page (page 2) — use map[index] for the color slot, matching original behaviour
      drawPiccoloAnswer2(
        data.page,
        data.variant,
        data.rect,
        label.right,
        map[data.index],
        answerFont,
        singleLineAnswers
      );
    }
  };

  // Use provided callbacks or defaults
  const drawQ = drawQuestion ?? defaultDrawQuestion;
  const drawA = drawAnswer ?? defaultDrawAnswer;

  // Seedable decoration drawing with scattered pastel shapes
  const drawDecoration = (page: PageGen, rect: PdfRect, seed: number) => {
    const rng = new Random(seed);
    const pastelColors = [
      PdfColor.fromHex('#FFB3BA'),
      PdfColor.fromHex('#FFDFBA'),
      PdfColor.fromHex('#FFFFBA'),
      PdfColor.fromHex('#BAFFC9'),
      PdfColor.fromHex('#BAE1FF'),
      PdfColor.fromHex('#E8BAFF'),
      PdfColor.fromHex('#FFC9DE'),
      PdfColor.fromHex('#C9FFE5'),
    ];

    const shapeCount = 3 + rng.next(0, 3);

    for (let i = 0; i < shapeCount; i++) {
      const color = pastelColors[rng.next(0, pastelColors.length)];
      const brush = new PdfBrush(color);
      const shapeType = rng.next(0, 4);
      const size = PdfUnit.FromMillimeters(8) + rng.nextDouble() * PdfUnit.FromMillimeters(15);

      const halfW = rect.width / 2;
      const halfH = rect.height / 2;
      const cx = rect.x + (i % 2) * halfW + rng.nextDouble() * halfW;
      const cy = rect.y + Math.floor(i / 2) * halfH + rng.nextDouble() * halfH;

      const minX = rect.x + size / 2;
      const maxX = rect.right - size / 2;
      const minY = rect.y + size / 2;
      const maxY = rect.bottom - size / 2;
      const clampX = Math.max(minX, Math.min(maxX, cx));
      const clampY = Math.max(minY, Math.min(maxY, cy));

      switch (shapeType) {
        case 0: // Circle
          page.drawEllipse(
            null,
            brush,
            new PdfRect(clampX - size / 2, clampY - size / 2, size, size)
          );
          break;
        case 1: // Square
          page.drawRectangle(
            null,
            brush,
            new PdfRect(clampX - size / 2, clampY - size / 2, size, size)
          );
          break;
        case 2: // Triangle
          const triPts = [
            new PdfPoint(clampX, clampY - size / 2),
            new PdfPoint(clampX - size / 2, clampY + size / 2),
            new PdfPoint(clampX + size / 2, clampY + size / 2),
          ];
          page.drawPolygon(null, brush, triPts);
          break;
        case 3: // Diamond
          const diaPts = [
            new PdfPoint(clampX, clampY - size / 2),
            new PdfPoint(clampX + size / 2, clampY),
            new PdfPoint(clampX, clampY + size / 2),
            new PdfPoint(clampX - size / 2, clampY),
          ];
          page.drawPolygon(null, brush, diaPts);
          break;
      }
    }
  };

  const minSpacing = PdfUnit.FromMillimeters(10);

  const drawMiniItem = (rect: PdfRect, index: number) => {
    if (index >= tuples.length) return;

    // Circle on LEFT, question text on RIGHT (matches C# DrawMiniItem)
    const circleRect = new PdfRect(rect.x + margin, rect.centerY - sz / 2, sz, sz);
    const rectQ = new PdfRect(rect.x + d, rect.y, rect.width - d, rect.height);

    const data1: LogicoData = {
      page: page1,
      rect: rectQ,
      index,
      invMap,
      isQuestion: true,
      isFront: true,
      variant,
    };
    const data2: LogicoData = {
      page: page2,
      rect: rectQ,
      index,
      invMap,
      isQuestion: true,
      isFront: false,
      variant,
    };

    drawQ(data1);
    drawQ(data2);

    if (drawOutline) {
      page1.drawRectangle(GUtil.basePen, null, rect);
      page2.drawRectangle(GUtil.basePen, null, rect);
    }

    if (drawCircle) {
      drawLogicoCircle(page1, variant, index, circleRect);
      drawLogicoCircle(page2, variant, index, circleRect);
    }
  };

  const drawBigItem = (rect: PdfRect, index: number) => {
    if (index >= tuples.length) return;

    // Circle at top-left corner, drawQuestion receives full rect (matches C# DrawBigItem)
    const circleRect = new PdfRect(rect.x + margin, rect.y + margin, sz, sz);

    const data1: LogicoData = {
      page: page1,
      rect,
      index,
      invMap,
      isQuestion: true,
      isFront: true,
      variant,
    };
    const data2: LogicoData = {
      page: page2,
      rect,
      index,
      invMap,
      isQuestion: true,
      isFront: false,
      variant,
    };

    drawQ(data1);
    drawQ(data2);

    if (drawOutline) {
      page1.drawRectangle(GUtil.basePen, null, rect);
      page2.drawRectangle(GUtil.basePen, null, rect);
    }

    if (drawCircle) {
      drawLogicoCircle(page1, variant, index, circleRect);
      drawLogicoCircle(page2, variant, index, circleRect);
    }
  };

  // Helper to compute smallest cell size for a mode
  // Returns PdfSize with smallest min(width, height) as controlling dimension
  const getCellSize = (
    mode: ELogicoMode,
    optionW: number,
    optionsH: number,
    answerCnt: number,
    miniCells: { x: number; y: number },
    circleD: number
  ): PdfSize => {
    const halfW = optionW / 2;
    const thirdW = optionW / 3;
    switch (mode) {
      case ELogicoMode.One:
        return new PdfSize(optionW, optionsH);
      case ELogicoMode.Mini:
        return new PdfSize(halfW - circleD, optionsH / answerCnt);
      case ELogicoMode.Rows:
        return new PdfSize(optionW - circleD, optionsH / answerCnt);
      case ELogicoMode.TwoColumns:
        return new PdfSize(optionW / miniCells.x, optionsH / miniCells.y);
      case ELogicoMode.Mode343: {
        const size = Math.min(thirdW, optionsH / 4);
        return new PdfSize(size, size);
      }
      case ELogicoMode.Mode23: {
        const size2 = halfW;
        const size3 = thirdW;
        const h = optionsH / 4;
        const s2 = Math.min(size2, h);
        const s3 = Math.min(size3, h);
        return new PdfSize(Math.min(s2, s3), Math.min(s2, s3));
      }
      case ELogicoMode.Diagonal:
      case ELogicoMode.ZigZag1:
      case ELogicoMode.ZigZag2: {
        const w = thirdW * 2;
        return new PdfSize(w, optionsH / answerCnt);
      }
      case ELogicoMode.Around:
        return new PdfSize(thirdW, optionsH / 4);
      case ELogicoMode.Random:
      case ELogicoMode.MiniRandom: {
        const cellSize = Math.min(halfW, optionsH / answerCnt);
        return new PdfSize(cellSize, cellSize);
      }
      case ELogicoMode.Aside: {
        const leftW = (optionW - PdfUnit.FromMillimeters(15)) / 2;
        const leftH = optionsH / 10;
        const middleW = PdfUnit.FromMillimeters(15);
        const middleH = optionsH / 10;
        const leftCell = Math.min(leftW, leftH);
        const middleCell = Math.min(middleW, middleH);
        return new PdfSize(Math.min(leftCell, middleCell), Math.min(leftCell, middleCell));
      }
      case ELogicoMode.Top:
      case ELogicoMode.Half:
      case ELogicoMode.PaperA5:
      default: {
        // Default / fallback: use mini cell size
        return new PdfSize(halfW - circleD, optionsH / answerCnt);
      }
    }
  };

  // Path drawing function - replicates C# Path(count) algorithm
  const path = (count: number) => {
    const size = optionsHeight / answerCount;
    const size0 = new PdfSize(size * 1.5, size);

    setWidth(new PdfSize(size0.width, size0.height));

    const pathX = optionRect.x + sz + margin * 2 + size0.width;
    const pathRect = new PdfRect(pathX, optionRect.y, answerPoint.x - pathX, optionsHeight);

    for (let index = 0; index < tuples.length; index++) {
      const cellY = optionRect.y + size0.height * index;
      const circleCenter = new PdfPoint(optionRect.x + margin + sz / 2, cellY + size0.height / 2);
      const cellRect = new PdfRect(
        optionRect.x + sz + margin * 2,
        cellY,
        size0.width,
        size0.height
      );

      drawQ({
        page: page1,
        rect: cellRect,
        index,
        invMap,
        isQuestion: true,
        isFront: true,
        variant,
      });
      drawQ({
        page: page2,
        rect: cellRect,
        index,
        invMap,
        isQuestion: true,
        isFront: false,
        variant,
      });

      if (drawCircle) {
        const circleRect = new PdfRect(circleCenter.x - sz / 2, circleCenter.y - sz / 2, sz, sz);
        drawLogicoCircle(page1, variant, index, circleRect);
        drawLogicoCircle(page2, variant, index, circleRect);
      }

      if (drawOutline) {
        page1.drawRectangle(GUtil.basePen, null, cellRect);
        page2.drawRectangle(GUtil.basePen, null, cellRect);
      }
    }

    const rnd = (max: number, exclude: number[]): number => {
      let r: number;
      do {
        r = Math.floor(data!.random.next(0, max));
      } while (exclude.includes(r));
      return r;
    };

    const rem = <T>(arr: T[], n: number): T[] => arr.splice(0, n);

    const t0 = Array.from({ length: answerCount }, () =>
      Math.floor(data!.random.next(0, count + 2))
    );
    const b0 = Array.from({ length: answerCount }, (_, i) => rnd(count + 2, [t0[invMap[i]]]));

    const t1 = Array.from({ length: answerCount }, (_, i) => i * (count + 2) + t0[i]);
    const b1 = Array.from({ length: answerCount }, (_, i) => invMap[i] * (count + 2) + b0[i]);

    const usedV = [...t1, ...b1];
    const remainV = Array.from({ length: answerCount * (count + 2) }, (_, i) => i).filter(
      (i) => !usedV.includes(i)
    );
    for (let i = remainV.length - 1; i > 0; i--) {
      const j = Math.floor(data!.random.next(0, i + 1));
      [remainV[i], remainV[j]] = [remainV[j], remainV[i]];
    }

    const remainH = Array.from({ length: answerCount * (count + 1) }, (_, i) => i);
    for (let i = remainH.length - 1; i > 0; i--) {
      const j = Math.floor(data!.random.next(0, i + 1));
      [remainH[i], remainH[j]] = [remainH[j], remainH[i]];
    }

    const hx: number[][] = [];
    for (let i = 0; i <= count; i++) hx.push(rem(remainH, answerCount).map((j) => j + 1));

    const vx: number[][] = [];
    for (let i = 0; i < count; i++) vx.push(rem(remainV, answerCount));

    const v: number[][] = [t1, ...vx, b1];
    const h0 = Array.from({ length: answerCount }, () => 0);
    const hLast = Array.from({ length: answerCount }, () => 1 + (count + 1) * answerCount);
    const h: number[][] = [h0, ...hx, hLast];

    const X = (i: number): number =>
      pathRect.x + (pathRect.width / ((count + 1) * answerCount + 1)) * i;
    const Y = (i: number): number => {
      const lane = Math.floor(i / (count + 2));
      const pos = i % (count + 2);
      return pathRect.y + lane * size + (pos + 1) * (size / (count + 3));
    };

    for (let j = 0; j < tuples.length; j++) {
      const points: PdfPoint[] = [];
      for (let i = 0; i <= count; i++) {
        points.push(new PdfPoint(X(h[i][j]), Y(v[i][j])));
        points.push(new PdfPoint(X(h[i + 1][j]), Y(v[i][j])));
        points.push(new PdfPoint(X(h[i + 1][j]), Y(v[i + 1][j])));
      }
      points.push(new PdfPoint(X(h[count + 2][j]), Y(v[count + 1][j])));
      page1.drawLines(GUtil.basePen, points);
      page2.drawLines(GUtil.basePen, points);
    }
  };

  // Draw based on mode
  switch (mode) {
    case ELogicoMode.One:
      setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
      drawQ({
        page: page1,
        rect: optionRect,
        index: 0,
        invMap,
        isQuestion: true,
        isFront: true,
        variant,
      });
      drawQ({
        page: page2,
        rect: optionRect,
        index: 0,
        invMap,
        isQuestion: true,
        isFront: false,
        variant,
      });
      break;

    case ELogicoMode.Mini:
      setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
      {
        const seed1 = Math.floor(Math.random() * 1000000);
        const seed2 = Math.floor(Math.random() * 1000000);

        const r1 = new PdfRect(
          optionRect.x + optionRect.width / 2,
          optionRect.y + optionRect.height / 10,
          optionRect.width / 2,
          (optionRect.height * 3) / 10
        );
        const r2 = new PdfRect(
          optionRect.x,
          optionRect.y + (optionRect.height * 6) / 10,
          optionRect.width / 2,
          (optionRect.height * 3) / 10
        );

        drawDecoration(page1, r1, seed1);
        drawDecoration(page2, r1, seed1);
        drawDecoration(page1, r2, seed2);
        drawDecoration(page2, r2, seed2);

        for (let i = 0; i < miniCellCount.y; i++) {
          if (i >= tuples.length) continue;
          const rect = new PdfRect(
            optionRect.x,
            optionRect.y + (i * optionRect.height) / answerCount,
            optionRect.width / 2,
            optionRect.height / answerCount
          );
          drawMiniItem(rect, i);
        }

        for (let i = 0; i < miniCellCount.y; i++) {
          const index = miniCellCount.y + i;
          if (index >= tuples.length) continue;
          const rect = new PdfRect(
            optionRect.x + optionRect.width / 2,
            optionRect.y + ((i + miniCellCount.y) * optionRect.height) / answerCount,
            optionRect.width / 2,
            optionRect.height / answerCount
          );
          drawMiniItem(rect, index);
        }
      }
      break;

    case ELogicoMode.Rows:
      setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
      for (let index = 0; index < tuples.length; index++) {
        const rect = new PdfRect(
          optionRect.x,
          optionRect.y + (index * optionRect.height) / answerCount,
          optionRect.width,
          optionRect.height / answerCount
        );
        drawMiniItem(rect, index);
      }
      break;

    case ELogicoMode.TwoColumns:
      setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
      for (let y = 0; y < miniCellCount.y; y++) {
        for (let x = 0; x < miniCellCount.x; x++) {
          const index = y * miniCellCount.x + x;
          const rect = new PdfRect(
            optionRect.x + (x * optionRect.width) / miniCellCount.x,
            optionRect.y + (y * optionRect.height) / miniCellCount.y,
            optionRect.width / miniCellCount.x,
            optionRect.height / miniCellCount.y
          );
          drawBigItem(rect, index);
        }
      }
      break;

    case ELogicoMode.Top:
      {
        const topSize = new PdfSize(optionRect.width, PdfUnit.FromMillimeters(40));
        const topRect = new PdfRect(optionRect.x, optionRect.y, topSize.width, topSize.height);
        const optionRectUpd = new PdfRect(
          optionRect.x,
          optionRect.y + topSize.height,
          optionRect.width,
          optionRect.height - topSize.height
        );
        const gridCellSize = new PdfSize(
          optionRectUpd.width / miniCellCount.x,
          optionRectUpd.height / miniCellCount.y
        );
        const topCell = Math.min(topSize.width, topSize.height);
        const gridCell = Math.min(gridCellSize.width, gridCellSize.height);
        setWidth(new PdfSize(Math.min(topCell, gridCell), Math.min(topCell, gridCell)));

        for (let y = 0; y < miniCellCount.y; y++) {
          for (let x = 0; x < miniCellCount.x; x++) {
            const index = y * miniCellCount.x + x;
            const rect = new PdfRect(
              optionRectUpd.x + (x * optionRectUpd.width) / miniCellCount.x,
              optionRectUpd.y + (y * optionRectUpd.height) / miniCellCount.y,
              optionRectUpd.width / miniCellCount.x,
              optionRectUpd.height / miniCellCount.y
            );
            drawBigItem(rect, index);
          }
        }

        drawQ({
          page: page1,
          rect: topRect,
          index: -1,
          invMap,
          isQuestion: true,
          isFront: true,
          variant,
        });
        drawQ({
          page: page2,
          rect: topRect,
          index: -1,
          invMap,
          isQuestion: true,
          isFront: false,
          variant,
        });
      }
      break;

    case ELogicoMode.Half:
      {
        const topSize = new PdfSize(optionRect.width, optionRect.height / 2);
        const topRect = new PdfRect(optionRect.x, optionRect.y, topSize.width, topSize.height);
        const optionRectUpd = new PdfRect(
          optionRect.x,
          optionRect.y + topSize.height,
          optionRect.width,
          optionRect.height - topSize.height
        );
        const gridCellSize = new PdfSize(
          optionRectUpd.width / miniCellCount.x,
          optionRectUpd.height / miniCellCount.y
        );
        const topCell = Math.min(topSize.width, topSize.height);
        const gridCell = Math.min(gridCellSize.width, gridCellSize.height);
        setWidth(new PdfSize(Math.min(topCell, gridCell), Math.min(topCell, gridCell)));

        for (let y = 0; y < miniCellCount.y; y++) {
          for (let x = 0; x < miniCellCount.x; x++) {
            const index = y * miniCellCount.x + x;
            const rect = new PdfRect(
              optionRectUpd.x + (x * optionRectUpd.width) / miniCellCount.x,
              optionRectUpd.y + (y * optionRectUpd.height) / miniCellCount.y,
              optionRectUpd.width / miniCellCount.x,
              optionRectUpd.height / miniCellCount.y
            );
            drawMiniItem(rect, index);
          }
        }

        drawQ({
          page: page1,
          rect: topRect,
          index: -1,
          invMap,
          isQuestion: true,
          isFront: true,
          variant,
        });
        drawQ({
          page: page2,
          rect: topRect,
          index: -1,
          invMap,
          isQuestion: true,
          isFront: false,
          variant,
        });
      }
      break;

    case ELogicoMode.Mode23:
      {
        const height = optionRect.height / 4;
        const size2 = optionRect.width / 2;
        const size3 = optionRect.width / 3;
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));

        drawBigItem(
          new PdfRect(optionRect.x + 0 * size2, optionRect.y + 0 * height, size2, height),
          0
        );
        drawBigItem(
          new PdfRect(optionRect.x + 1 * size2, optionRect.y + 0 * height, size2, height),
          1
        );
        drawBigItem(
          new PdfRect(optionRect.x + 0 * size3, optionRect.y + 1 * height, size3, height),
          2
        );
        drawBigItem(
          new PdfRect(optionRect.x + 1 * size3, optionRect.y + 1 * height, size3, height),
          3
        );
        drawBigItem(
          new PdfRect(optionRect.x + 2 * size3, optionRect.y + 1 * height, size3, height),
          4
        );
        drawBigItem(
          new PdfRect(optionRect.x + 0 * size2, optionRect.y + 2 * height, size2, height),
          5
        );
        drawBigItem(
          new PdfRect(optionRect.x + 1 * size2, optionRect.y + 2 * height, size2, height),
          6
        );
        drawBigItem(
          new PdfRect(optionRect.x + 0 * size3, optionRect.y + 3 * height, size3, height),
          7
        );
        drawBigItem(
          new PdfRect(optionRect.x + 1 * size3, optionRect.y + 3 * height, size3, height),
          8
        );
        drawBigItem(
          new PdfRect(optionRect.x + 2 * size3, optionRect.y + 3 * height, size3, height),
          9
        );
      }
      break;

    case ELogicoMode.Pyramid:
      {
        const size = optionRect.width / 4;
        const top = optionRect.y + (optionRect.height - 4 * size) / 2;
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));

        drawBigItem(new PdfRect(optionRect.x + size * 1.5, top + 0 * size, size, size), 0);
        drawBigItem(new PdfRect(optionRect.x + size * 1.0, top + 1 * size, size, size), 1);
        drawBigItem(new PdfRect(optionRect.x + size * 2.0, top + 1 * size, size, size), 2);
        drawBigItem(new PdfRect(optionRect.x + size * 0.5, top + 2 * size, size, size), 3);
        drawBigItem(new PdfRect(optionRect.x + size * 1.5, top + 2 * size, size, size), 4);
        drawBigItem(new PdfRect(optionRect.x + size * 2.5, top + 2 * size, size, size), 5);
        drawBigItem(new PdfRect(optionRect.x + size * 0.0, top + 3 * size, size, size), 6);
        drawBigItem(new PdfRect(optionRect.x + size * 1.0, top + 3 * size, size, size), 7);
        drawBigItem(new PdfRect(optionRect.x + size * 2.0, top + 3 * size, size, size), 8);
        drawBigItem(new PdfRect(optionRect.x + size * 3.0, top + 3 * size, size, size), 9);
      }
      break;

    case ELogicoMode.Mode343:
      {
        const size = Math.min(optionRect.width / 3, optionRect.height / 4);
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
        const pp = new PdfPoint(
          optionRect.x + (optionRect.width - 3 * size) / 2,
          optionRect.y + (optionRect.height - 4 * size) / 2
        );

        drawBigItem(new PdfRect(pp.x + size * 0, pp.y + 0.5 * size, size, size), 0);
        drawBigItem(new PdfRect(pp.x + size * 0, pp.y + 1.5 * size, size, size), 1);
        drawBigItem(new PdfRect(pp.x + size * 0, pp.y + 2.5 * size, size, size), 2);
        drawBigItem(new PdfRect(pp.x + size * 1, pp.y + 0.0 * size, size, size), 3);
        drawBigItem(new PdfRect(pp.x + size * 1, pp.y + 1.0 * size, size, size), 4);
        drawBigItem(new PdfRect(pp.x + size * 1, pp.y + 2.0 * size, size, size), 5);
        drawBigItem(new PdfRect(pp.x + size * 1, pp.y + 3.0 * size, size, size), 6);
        drawBigItem(new PdfRect(pp.x + size * 2, pp.y + 0.5 * size, size, size), 7);
        drawBigItem(new PdfRect(pp.x + size * 2, pp.y + 1.5 * size, size, size), 8);
        drawBigItem(new PdfRect(pp.x + size * 2, pp.y + 2.5 * size, size, size), 9);
      }
      break;

    case ELogicoMode.Random:
      {
        let size = new PdfSize(optionRect.width / 4, optionRect.width / 4);
        if (variant === ELogicoVariant.Maximo) {
          size = new PdfSize(optionRect.width / 6, optionRect.width / 6);
        }
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
        const minSpacing = PdfUnit.FromMillimeters(5);
        const placed = placeObjectsCore(
          data?.random ?? new Random(),
          optionRect,
          size,
          Array.from({ length: answerCount }, (_, i) => i),
          { minSpacing }
        );
        for (const obj of placed) {
          drawBigItem(obj.rect, obj.data);
        }
      }
      break;

    case ELogicoMode.Around:
      {
        const size = new PdfSize(optionRect.width / 3, optionRect.height / 4);
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
        drawBigItem(
          new PdfRect(
            optionRect.x + 0 * size.width,
            optionRect.y + 0 * size.height,
            size.width,
            size.height
          ),
          0
        );
        drawBigItem(
          new PdfRect(
            optionRect.x + 1 * size.width,
            optionRect.y + 0 * size.height,
            size.width,
            size.height
          ),
          1
        );
        drawBigItem(
          new PdfRect(
            optionRect.x + 2 * size.width,
            optionRect.y + 0 * size.height,
            size.width,
            size.height
          ),
          2
        );
        drawBigItem(
          new PdfRect(
            optionRect.x + 0 * size.width,
            optionRect.y + 1 * size.height,
            size.width,
            size.height
          ),
          3
        );
        drawBigItem(
          new PdfRect(
            optionRect.x + 2 * size.width,
            optionRect.y + 1 * size.height,
            size.width,
            size.height
          ),
          4
        );
        drawBigItem(
          new PdfRect(
            optionRect.x + 0 * size.width,
            optionRect.y + 2 * size.height,
            size.width,
            size.height
          ),
          5
        );
        drawBigItem(
          new PdfRect(
            optionRect.x + 2 * size.width,
            optionRect.y + 2 * size.height,
            size.width,
            size.height
          ),
          6
        );
        drawBigItem(
          new PdfRect(
            optionRect.x + 0 * size.width,
            optionRect.y + 3 * size.height,
            size.width,
            size.height
          ),
          7
        );
        drawBigItem(
          new PdfRect(
            optionRect.x + 1 * size.width,
            optionRect.y + 3 * size.height,
            size.width,
            size.height
          ),
          8
        );
        drawBigItem(
          new PdfRect(
            optionRect.x + 2 * size.width,
            optionRect.y + 3 * size.height,
            size.width,
            size.height
          ),
          9
        );
      }
      break;

    case ELogicoMode.Diagonal:
      {
        const size = new PdfSize((optionRect.width / 3) * 2, optionRect.height / answerCount);
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
        for (let i = 0; i < answerCount; i++) {
          drawMiniItem(
            new PdfRect(
              optionRect.x + ((optionRect.width - size.width) / answerCount) * i,
              optionRect.y + size.height * i,
              size.width,
              size.height
            ),
            i
          );
        }
      }
      break;

    case ELogicoMode.ZigZag1:
      {
        const size = new PdfSize((optionRect.width / 3) * 2, optionRect.height / answerCount);
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
        for (let i = 0; i < answerCount; i++) {
          const x = (i % 2) * (optionRect.width - size.width);
          drawMiniItem(
            new PdfRect(optionRect.x + x, optionRect.y + size.height * i, size.width, size.height),
            i
          );
        }
      }
      break;

    case ELogicoMode.ZigZag2:
      {
        const size = new PdfSize((optionRect.width / 3) * 2, optionRect.height / answerCount);
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
        for (let i = 0; i < answerCount; i++) {
          const x = (Math.floor(i / 2) % 2) * (optionRect.width - size.width);
          drawMiniItem(
            new PdfRect(optionRect.x + x, optionRect.y + size.height * i, size.width, size.height),
            i
          );
        }
      }
      break;

    case ELogicoMode.MiniRandom:
      {
        const size = new PdfSize((optionRect.width / 3) * 2, optionRect.height / answerCount);
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
        for (let i = 0; i < answerCount; i++) {
          const x = Math.random() * (optionRect.width - size.width);
          drawMiniItem(
            new PdfRect(optionRect.x + x, optionRect.y + size.height * i, size.width, size.height),
            i
          );
        }
      }
      break;

    case ELogicoMode.Aside:
      {
        const middleSize = new PdfSize(PdfUnit.FromMillimeters(15), optionRect.height / 10);
        const size = new PdfSize((optionRect.width - middleSize.width) / 2, optionRect.height / 10);
        setWidth(getCellSize(mode, optionRect.width, optionsHeight, answerCount, miniCellCount, d));
        for (let index = 0; index < 10; index++) {
          const r1 = new PdfRect(
            optionRect.x,
            optionRect.y + size.height * index,
            size.width,
            size.height
          );
          const r2 = new PdfRect(
            optionRect.x + size.width + middleSize.width,
            optionRect.y + size.height * index,
            size.width,
            size.height
          );
          const r0 = new PdfRect(r1.right, r1.y, middleSize.width, middleSize.height);

          drawQ({
            page: page1,
            rect: r1,
            index,
            invMap,
            isQuestion: true,
            isFront: true,
            variant,
            subIndex: 0,
          });
          drawQ({
            page: page1,
            rect: r2,
            index,
            invMap,
            isQuestion: true,
            isFront: true,
            variant,
            subIndex: 1,
          });
          drawQ({
            page: page2,
            rect: r1,
            index,
            invMap,
            isQuestion: true,
            isFront: false,
            variant,
            subIndex: 0,
          });
          drawQ({
            page: page2,
            rect: r2,
            index,
            invMap,
            isQuestion: true,
            isFront: false,
            variant,
            subIndex: 1,
          });

          if (drawCircle) {
            drawLogicoCircle(page1, variant, index, r0);
            drawLogicoCircle(page2, variant, index, r0);
          }

          if (drawOutline) {
            page1.drawRectangle(GUtil.basePen, null, r1);
            page1.drawRectangle(GUtil.basePen, null, r2);
            page2.drawRectangle(GUtil.basePen, null, r1);
            page2.drawRectangle(GUtil.basePen, null, r2);
          }
        }
      }
      break;

    case ELogicoMode.Path0:
    case ELogicoMode.Path1:
    case ELogicoMode.Path2: {
      const cnt = mode === ELogicoMode.Path0 ? 0 : mode === ELogicoMode.Path1 ? 1 : 2;
      path(cnt);
      break;
    }
  }

  // Draw answer column
  for (let i = 0; i < answerCount; i++) {
    if (i >= tuples.length) continue;
    const answerRect = new PdfRect(
      answerPoint.x,
      answerPoint.y + i * answerHeight,
      answerWidthReal,
      answerHeight
    );

    // Draw answer background and text — always route through drawA so answerFont is applied.
    // drawA is defaultDrawAnswer (which includes borders) or a custom callback.
    const answerData1: LogicoData = {
      page: page1,
      rect: answerRect,
      index: i,
      invMap,
      isQuestion: false,
      isFront: true,
      variant,
    };
    const answerData2: LogicoData = {
      page: page2,
      rect: answerRect,
      index: i,
      invMap,
      isQuestion: false,
      isFront: false,
      variant,
    };
    drawA(answerData1);
    drawA(answerData2);

    if (drawAnswer && drawOutline) {
      // Custom callbacks don't draw borders themselves, so add them when requested.
      page1.drawRectangle(GUtil.basePen, null, answerRect);
      page2.drawRectangle(GUtil.basePen, null, answerRect);
    }
  }
}

/**
 * Draw Logico puzzle in PaperA5 mode - A5-sized puzzle on landscape A4 paper (split in half).
 * Port of C# DrawLogicoPaperA5()
 */
function drawLogicoPaperA5(
  pdf: PdfGen,
  variant: ELogicoVariant,
  tuples: GTuple[],
  map: number[],
  pageIndex: number,
  answerWidthOverride: number | undefined,
  drawOutline: boolean,
  drawCircle: boolean,
  id: string | undefined,
  data: GenData | undefined,
  singleLineAnswers: boolean | undefined,
  drawQuestion: ((data: LogicoData) => void) | undefined,
  drawAnswer: ((data: LogicoData) => void) | undefined,
  calcQuestionSize: ((size: PdfSize) => void) | undefined,
  calcAnswerSize: ((size: PdfSize) => void) | undefined
): void {
  const page1 = pdf.getPage(pageIndex);
  const page2 = pdf.getPage(pageIndex + 1);

  const margin = PdfUnit.FromMillimeters(5);
  const margin1 = PdfUnit.FromMillimeters(2);
  const halfA4Height = PdfSize.A4.height / 2;
  const a4Width = PdfSize.A4.width;

  // rect is the left-half of the page (questions area), oriented in portrait
  const rect = new PdfRect(margin, margin, halfA4Height - 2 * margin, a4Width - 2 * margin);

  // Generate inverse map (same as main drawLogico)
  const invMap: number[] = map
    .map((val, idx) => ({ val, idx }))
    .sort((a, b) => a.val - b.val)
    .map((x) => x.idx);

  // Draw horizontal cut line at page center
  page1.drawLine(GUtil.cutPen, new PdfPoint(0, halfA4Height), new PdfPoint(a4Width, halfA4Height));

  // Save state for both pages before transforms
  page1.save();
  page2.save();

  // Map landscape top-down system to portrait PDF coordinates, accounting for PageGen's getXY (Y-flip).
  // x_logic, y_logic -> pdf_x = x_logic, pdf_y = a4Height - y_logic.
  // We want to map these to portrait coordinates such that:
  // x_final = a4Width - y_logic
  // y_final = a4Height - x_logic
  // Substituting x_logic = pdf_x and y_logic = a4Height - pdf_y:
  // x_final = a4Width - (a4Height - pdf_y) = pdf_y + a4Width - a4Height
  // y_final = a4Height - pdf_x
  // This gives matrix [0, -1, 1, 0, e, f] where e = a4Width - a4Height, f = a4Height.
  const e = a4Width - PdfSize.A4.height;
  const f = PdfSize.A4.height;

  page1.page.pushOperators(concatTransformationMatrix(0, -1, 1, 0, e, f));
  page2.page.pushOperators(concatTransformationMatrix(0, -1, 1, 0, e, f));

  // Draw question area border
  page1.drawRectangle(GUtil.basePen, null, rect);

  const aWidth = answerWidthOverride ?? rect.width / 2;
  const qWidth = rect.width - aWidth;
  const circleSize = new PdfSize(PdfUnit.FromMillimeters(10), PdfUnit.FromMillimeters(10));

  calcQuestionSize?.(new PdfSize(qWidth, rect.height));

  const answerCount = logicoCount(variant);

  // Compute answer font (matching C# AdjustFont approach)
  const allAnswerTexts = tuples.map((t) => [t.right.getText()]);
  const answerFont =
    allAnswerTexts.length > 0
      ? page1.adjustFontMulti(
          allAnswerTexts,
          GUtil.baseFont,
          new PdfSize(aWidth - 2 * margin1, rect.height / answerCount - 2 * margin1)
        )
      : GUtil.baseFont;

  // Draw question rows
  for (let index = 0; index < answerCount; index++) {
    const rowHeight = rect.height / answerCount;
    const rQ = new PdfRect(rect.x, rect.y + index * rowHeight, qWidth, rowHeight);
    const rA = new PdfRect(rect.x + qWidth, rect.y + index * rowHeight, aWidth, rowHeight);

    if (index >= tuples.length) break;

    const questionData: LogicoData = {
      page: page1,
      rect: rQ,
      index,
      invMap,
      isQuestion: true,
      isFront: true,
      variant,
    };
    if (drawQuestion) {
      drawQuestion(questionData);
    } else {
      const label = tuples[index].left;
      page1.drawLabel(label, { rect: rQ });
    }

    if (drawCircle) {
      const c = new PdfRect(rQ.x + margin1, rQ.y + margin1, circleSize.width, circleSize.height);
      drawLogicoCircle(page1, variant, index, c);
    }

    if (drawOutline) {
      page1.drawRectangle(GUtil.basePen, null, rQ);
      page1.drawRectangle(GUtil.basePen, null, rA);
    }
  }

  // Draw answer rows on page 2 (bottom half position)
  for (let index = 0; index < answerCount; index++) {
    const rowHeight = rect.height / answerCount;
    // Answers are positioned at right side of the rotated page 2
    const rA = new PdfRect(
      (PdfSize.A4.height * 3) / 4 - aWidth / 2,
      margin + index * rowHeight,
      aWidth,
      rowHeight
    );

    if (drawAnswer) {
      const answerData1: LogicoData = {
        page: page1,
        rect: rA,
        index,
        invMap,
        isQuestion: false,
        isFront: true,
        variant,
      };
      const answerData2: LogicoData = {
        page: page2,
        rect: rA,
        index,
        invMap,
        isQuestion: false,
        isFront: false,
        variant,
      };
      drawAnswer(answerData1);
      drawAnswer(answerData2);
    } else {
      // Default: draw PiccoloAnswer1 style (just text + border, no color rect)
      const label = tuples[index].right;
      page1.drawLabel(label, { rect: rA, font: answerFont, singleLine: singleLineAnswers });
      page1.drawRectangle(GUtil.basePen, null, rA);

      // Page 2: rotated 180 degrees for answer key
      page2.save();
      page2.rotateAtTransform(180, new PdfPoint(rA.centerX, rA.centerY));
      const reversedIndex = answerCount - index - 1;
      const reversedLabel = tuples[reversedIndex].right;
      page2.drawLabel(reversedLabel, { rect: rA, font: answerFont, singleLine: singleLineAnswers });
      page2.drawRectangle(GUtil.basePen, null, rA);
      page2.restore();
    }

    if (drawCircle) {
      const c = new PdfRect(rA.x + margin1, rA.y + margin1, circleSize.width, circleSize.height);
      // Page 2 shows the mapped circle color for the reversed answer
      const reversedIndex = answerCount - index - 1;
      drawLogicoCircle(page2, variant, map[reversedIndex], c);
    }

    if (drawOutline) {
      page1.drawRectangle(GUtil.cutPen, null, rA);
      page2.drawRectangle(GUtil.cutPen, null, rA);
    }
  }

  // Restore graphics state
  page1.restore();
  page2.restore();
}
